"use client";
import {
  AnchorDayPicker,
  AnchorDaysObject
} from "@/ui/controls/DatePicker/AnchorDayPicker";
import { FormErrorMessage } from "@/ui/feedback/FormMessages/FormErrorMessage";
import {
  defaultValues,
  useCreatePermissionProvider
} from "@/features/permissions/components/CreatePermission/CreatePermissionProvider";
import { TeamsPermissionList } from "@/features/permissions/components/TeamsPermissionList";
import { UsersPermissionList } from "@/features/permissions/components/UsersPermissionList";
import { HookFormCalendarInput } from "@gettactic/components/src/form/FormCalendarInput/HookFormCalendarInput";
import { HookFormToggleInput } from "@gettactic/components/src/form/ToggleInput/HookFormToggleInput";
import { Stack } from "@gettactic/components/src/primitives/Stack";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from "@cmp-src/primitives/tabs";
import { addMonths } from "date-fns";
import React, { useMemo } from "react";
import { useTabState } from "reakit/Tab";
const defaultAnchorDays: AnchorDaysObject = {
  sunday: false,
  monday: false,
  tuesday: false,
  wednesday: false,
  thursday: false,
  friday: false,
  saturday: false
};

export function CreatePermissionForm({ isOpen }: { isOpen: boolean }) {
  const { form } = useCreatePermissionProvider();

  const permanentAssignment = form.watch("permanentAssignment");
  const noExpirationDate = form.watch("noExpirationDate");
  const anchorDays = form.watch("anchorDays");
  const since = form.watch("since");
  const until = form.watch("until");

  const tab = useTabState();

  const today = new Date();

  const currentDayIdx = today.getDay();

  const minMaxDates = useMemo(() => {
    const permanentOrNoExpirationDate = permanentAssignment || noExpirationDate;
    const minDate = permanentOrNoExpirationDate ? undefined : since || today;

    let maxDate: Date | undefined = permanentOrNoExpirationDate
      ? undefined
      : until || undefined;

    if (!permanentOrNoExpirationDate && !maxDate) {
      maxDate = addMonths(today, 3);
    }

    return { minDate, maxDate };
  }, [since, until, permanentAssignment, noExpirationDate]);

  React.useEffect(() => {
    if (permanentAssignment) {
      form.setValue("anchorDays", defaultAnchorDays);
    }
  }, [permanentAssignment]);

  React.useEffect(() => {
    if (noExpirationDate || permanentAssignment) {
      form.setValue("until", null);
    }
    if (!permanentAssignment && !noExpirationDate) {
      form.setValue("until", defaultValues.until);
    }

    // When these constraints change, ensure the component re-renders
    // This allows the AnchorDayPicker to update its disabled states
    form.trigger("anchorDays");
  }, [noExpirationDate, permanentAssignment, defaultValues.until, form]);

  React.useEffect(() => {
    if (!isOpen) {
      // to reset on modal close
      tab.setSelectedId("users");
    }
  }, [isOpen]);

  // Initialize anchorDays with default values if not already set
  React.useEffect(() => {
    if (!anchorDays) {
      form.setValue("anchorDays", defaultAnchorDays);
    }
  }, []);

  const users = form.watch("users", []);
  const teams = form.watch("teams", []);

  const tabs: {
    id: string;
    title: string;
    render: () => React.ReactNode | null;
  }[] = [
    {
      id: "users",
      title: `Users (${users.length})`,
      render: () => (
        <UsersPermissionList
          ids={users}
          add={(id: string) => {
            form.setValue("users", [...users, id]);
          }}
          remove={(id: string) => {
            form.setValue(
              "users",
              users.filter((x) => x !== id)
            );
          }}
        />
      )
    },
    {
      id: "teams",
      title: `Teams (${teams.length})`,
      render: () => (
        <TeamsPermissionList
          ids={teams}
          add={(id: string) => {
            form.setValue("teams", [...teams, id]);
          }}
          remove={(id: string) => {
            form.setValue(
              "teams",
              teams.filter((x) => x !== id)
            );
          }}
        />
      )
    }
  ];

  return (
    <Stack className="flex-auto overflow-hidden">
      <div className="flex gap-4">
        <HookFormToggleInput
          form={form}
          name="permanentAssignment"
          label="Permanent Assignment"
          togglePosition="left"
        />
        {!permanentAssignment && (
          <HookFormToggleInput
            form={form}
            name="noExpirationDate"
            label="No Expiration Date"
            togglePosition="left"
          />
        )}
      </div>

      {!permanentAssignment && (
        <>
          <div className="flex gap-4">
            <HookFormCalendarInput
              form={form}
              className={"w-1/2"}
              name="since"
              label="Start Date"
              variant="outlined"
              isRequired={!permanentAssignment}
              setValueOnApply={false}
            />
            {!noExpirationDate && (
              <HookFormCalendarInput
                className={"w-1/2"}
                form={form}
                name="until"
                label="End Date"
                variant="outlined"
                isRequired={!permanentAssignment && !noExpirationDate}
                setValueOnApply={false}
              />
            )}
          </div>

          <div>
            <label
              className="mb-1 block font-medium text-sm"
              htmlFor="anchorDays"
            >
              Anchor Days
            </label>
            <AnchorDayPicker
              form={form}
              anchorDays={anchorDays}
              currentDayIdx={currentDayIdx}
              singleSelect={false}
              name="anchorDays"
              minDate={minMaxDates.minDate}
              maxDate={minMaxDates.maxDate}
            />
            <FormErrorMessage
              errors={form.formState.errors}
              fieldName={"anchorDays"}
            />
          </div>
        </>
      )}

      <Tabs defaultValue={tabs[0]?.id || ""}>
        <TabsList
          aria-label="Permission tabs"
          className="flex flex-none gap-8 border-neutral-200 border-b text-base"
        >
          {tabs.map((tabDef) => (
            <TabsTrigger
              key={tabDef.id}
              value={tabDef.id}
              variant="minimalistic"
              type="button"
            >
              {tabDef.title}
            </TabsTrigger>
          ))}
        </TabsList>
        {tabs.map((tabDef) => (
          <TabsContent key={tabDef.id} value={tabDef.id}>
            {tabDef.render()}
          </TabsContent>
        ))}
      </Tabs>
    </Stack>
  );
}
